import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FileWarning, Loader2, Lock, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAgreementStore } from '@/modules/agreements/store/agreementStore';
import { useAgreements } from '@/modules/agreements/hooks/useAgreements';
import { useConsent } from '@/hooks/useConsent';
import { useAgreementInfo } from '../hooks/useAgreementInfo';
import { usePunchcardConfig } from '../hooks/usePunchcardConfig';
import { useSubmitPunchcard } from '../hooks/useSubmitPunchcard';
import { useGeolocation } from '../hooks/useGeolocation';
import { usePunchcardClock } from '../hooks/usePunchcardClock';
import { usePunchcardCollects } from '../hooks/usePunchcardCollects';
import { AgreementInfoCard } from '../components/AgreementInfoCard';
import { ConsentGate } from '../components/ConsentGate';
import { PunchcardRegisterCard } from '../components/PunchcardRegisterCard';
import { TodayCollectsList } from '../components/TodayCollectsList';
import type { CameraPreviewHandle } from '../components/CameraPreview';
import { GEOLOCATION_POLICY } from '../constants';
import type { CheckType } from '../types';
import type { ClassifiedError } from '../utils/errors';
import { classifySubmitError } from '../utils/errors';
import { applyGeolocationPolicy } from '../utils/geolocationPolicy';
import { getLocalISOStringWithOffset, getTimezone, getTimezoneRegionAndOffset } from '../utils/formatters';
import { getTtsPrefs, playNotificationSound, speakTTS } from '../utils/audio';
import { notify } from '../utils/notify';

const globalWithNotify = globalThis as typeof globalThis & {
  pushNotification?: (type: string, message: string) => void;
};
function triggerPushNotification(type: string, message: string): void {
  globalWithNotify.pushNotification?.(type, message);
}

/**
 * Punchcard registration page: gates access behind geolocation/photo consent,
 * resolves the active agreement's punchcard config, and submits punches following
 * the agreement's geolocation policy — `required` demands a precise location,
 * `flexible` allows submission under contingency (timeout, unavailable sensor, or
 * insufficient accuracy, all of them with the browser permission verified as
 * granted) while recording that flag for audit, and `dispensed` never asks for a
 * location.
 */
export default function PunchcardCardPage() {
  const { activeAgreement, activeAgreementUuid } = useAgreementStore();
  const agreementsQuery = useAgreements();
  const { geoConsent, photoConsent, grantConsent } = useConsent();
  const config = usePunchcardConfig(activeAgreement?.context);
  const geoNeeded = config.geolocation !== GEOLOCATION_POLICY.DISPENSED;
  const {
    retrying: geoRetrying,
    refresh: refreshLocation,
    locationElapsedMs,
    locationSource,
    ...geoReading
  } = useGeolocation(
    geoNeeded && geoConsent,
  );
  const geoStatus = geoReading.status;
  const geoGate = applyGeolocationPolicy(config.geolocation, geoReading);
  const { data: agreementInfo, isLoading: infoLoading, isError: infoError } = useAgreementInfo(
    activeAgreement?.uuid,
  );
  const { data: collects = [] } = usePunchcardCollects(activeAgreement?.uuid);
  const submitMutation = useSubmitPunchcard();
  const now = usePunchcardClock();
  const navigate = useNavigate();
  const cameraRef = useRef<CameraPreviewHandle>(null);

  const [type, setType] = useState<CheckType>('entrando');
  const [submitError, setSubmitError] = useState<ClassifiedError | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    document.title = 'Registrar';
  }, []);

  // Contingência é um estado permitido: avisa, não reporta erro. `permission-prompt`
  // também avisa: bloqueia o registro, mas é transitório — o hook refaz a leitura
  // assim que a permissão for concedida. Só o que é terminal para o trabalhador
  // (permissão negada, navegador sem a API) é de fato um erro.
  useEffect(() => {
    if (!geoGate.error) return;
    const recoverable = geoGate.contingency || geoStatus === 'permission-prompt';
    notify(recoverable ? 'warning' : 'error', geoGate.error);
  }, [geoGate.error, geoGate.contingency, geoStatus]);

  const pendingConsent: 'geo' | 'photo' | null =
    geoNeeded && !geoConsent ? 'geo' : config.photoRequired && !photoConsent ? 'photo' : null;

  // Acordo persistido (uuid em localStorage) ainda não re-hidratado: nunca cair no card de
  // registro com o botão morto enquanto o acordo real não chega. Trata sucesso-mas-ainda-não-
  // hidratado como carregando também, para não piscar o card entre o resolve da query e o
  // efeito de useAgreementHydration.
  if (activeAgreementUuid && !activeAgreement) {
    if (agreementsQuery.isError) {
      return (
        <main className="space-y-4 px-4 py-6" id="main-content" role="main" aria-label="Conteúdo principal">
          <div
            role="alert"
            aria-live="assertive"
            className="flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
          >
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
            <p className="text-sm text-red-800">Não foi possível carregar o acordo.</p>
          </div>
          <Button onClick={() => agreementsQuery.refetch()} aria-label="Tentar novamente">
            Tentar novamente
          </Button>
        </main>
      );
    }
    return (
      <div role="status" aria-live="polite" className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-label="Carregando acordo" />
      </div>
    );
  }

  // Nenhum acordo foi escolhido (ou foi limpo): não há acordo "desabilitado", há ausência de
  // acordo — mensagem e CTA diferentes da tela de "Registro não autorizado" abaixo.
  if (!activeAgreementUuid && !activeAgreement) {
    return (
      <div className="flex flex-col items-center px-6 py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
          <FileWarning className="h-8 w-8 text-slate-300" aria-hidden="true" />
        </div>
        <h2 className="mb-2 text-sm font-semibold text-slate-900">Nenhum acordo selecionado</h2>
        <p className="mb-6 text-xs text-slate-500">Selecione um acordo para registrar o ponto.</p>
        <Button onClick={() => navigate('/agreements')}>Selecionar acordo</Button>
      </div>
    );
  }

  if (activeAgreement && !config.writeEnabled) {
    return (
      <div className="flex flex-col items-center px-6 py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
          <Lock className="h-8 w-8 text-slate-300" aria-hidden="true" />
        </div>
        <h2 className="mb-2 text-sm font-semibold text-slate-900">Registro não autorizado</h2>
        <p className="mb-6 text-xs text-slate-500">
          Este acordo não permite o registro de ponto por este aplicativo.
        </p>
        <Button variant="outline" onClick={() => navigate('/punchcard/history')}>
          Ver histórico
        </Button>
      </div>
    );
  }

  if (pendingConsent) {
    const grant = () => grantConsent(pendingConsent === 'geo' ? 'geolocation' : 'photo');
    return <ConsentGate consentType={pendingConsent} onGrant={grant} />;
  }

  const handleSubmit = () => {
    if (!activeAgreement) return;
    if (geoGate.blocked || geoGate.awaitingLocation) return;

    let photo: string | null = null;
    if (config.photoRequired && photoConsent && cameraRef.current) {
      photo = cameraRef.current.capture();
    }

    setSubmitError(null);
    submitMutation.mutate(
      {
        agreementUuid: activeAgreement.uuid,
        datetime: getLocalISOStringWithOffset(now),
        check: type === 'entrando' ? 'in' : 'out',
        location: geoGate.location,
        contingency: geoGate.contingency,
        contingencyReason: geoGate.contingencyReason,
        accuracy: geoGate.accuracy,
        locationElapsedMs: geoNeeded ? locationElapsedMs : null,
        locationSource: geoNeeded ? locationSource : null,
        timezone: getTimezone(),
        photo,
      },
      {
        onSuccess: () => {
          playNotificationSound();
          const prefs = getTtsPrefs();
          if (prefs.notifications && prefs.tts) {
            speakTTS(
              type === 'entrando' ? prefs.ttsCheckIn : prefs.ttsCheckOut,
              prefs.language,
            );
          }
          setSuccessOpen(true);
          setTimeout(() => navigate('/punchcard/history'), 2000);
        },
        onError: (error) => {
          const classified = classifySubmitError(error);
          setSubmitError(classified);
          triggerPushNotification(classified.severity, classified.message);
        },
      },
    );
  };

  return (
    <>
      {successOpen && (
        <div
          role="status"
          aria-live="polite"
          className="fixed left-4 right-4 top-4 z-50 flex items-center gap-3 rounded-2xl bg-green-600 px-4 py-3 text-sm font-semibold text-white shadow-xl"
        >
          <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
          Ponto registrado com sucesso!
        </div>
      )}
      <AgreementInfoCard info={agreementInfo} loading={infoLoading} hasError={infoError} />
      <PunchcardRegisterCard
        now={now}
        location={geoGate.location}
        geoStatus={geoStatus}
        geoPolicy={config.geolocation}
        geoBlocked={geoGate.blocked}
        geoAwaitingLocation={geoGate.awaitingLocation}
        geoRetrying={geoRetrying}
        timezoneDisplay={getTimezoneRegionAndOffset()}
        photoRequired={config.photoRequired}
        photoConsent={photoConsent}
        cameraRef={cameraRef}
        type={type}
        submitting={submitMutation.isPending}
        submitError={submitError}
        writeEnabled={config.writeEnabled}
        onTypeChange={setType}
        onSubmit={handleSubmit}
        onRetryLocation={refreshLocation}
      />
      <div className="px-6 pb-8">
        <TodayCollectsList collects={collects} />
      </div>
    </>
  );
}
